import React from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, HomeIcon, Building2Icon } from 'lucide-react';
import { LightingCategoryCard } from '../components/christmas/LightingCategoryCard';
import { DecorationServicesSection } from '../components/christmas/DecorationServicesSection';
import { EstimateFormSection } from '../components/christmas/estimate/EstimateFormSection';
import { ChristmasAboutSection } from '../components/christmas/sections/ChristmasAboutSection';
import { ChristmasProcessSection } from '../components/christmas/sections/ChristmasProcessSection';
import { ChristmasTestimonialsSection } from '../components/christmas/sections/ChristmasTestimonialsSection';
import { ChristmasGallerySection } from '../components/christmas/sections/ChristmasGallerySection';
import { ChristmasWhyChooseSection } from '../components/christmas/sections/ChristmasWhyChooseSection';
import { ChristmasFaqSection } from '../components/christmas/sections/ChristmasFaqSection';
import { ChristmasFooter } from '../components/christmas/sections/ChristmasFooter';
import { christmasHeroImage, lightingCategories } from '../data/christmasLighting';
import { christmasQuoteHref } from '../data/christmasContacts';
const ease: [number, number, number, number] = [0.22, 1, 0.36, 1];
export function ChristmasLights() {
  return <>
      <main id="main" className="w-full bg-cream" data-id="element-406" data-mp="1ov6mf198ws3r">
        <section aria-labelledby="christmas-title" className="relative isolate overflow-hidden bg-forest-900" data-id="element-407" data-mp="114aodme750o2">
          <img src={christmasHeroImage} alt="Elegant home at night decorated with roofline lights, wreaths, garlands, lit bushes and trees, and stake lights" className="absolute inset-0 -z-20 h-full w-full object-cover object-[65%_center]" data-id="element-408" data-mp="w6itcji98yfn" />
          <div aria-hidden="true" className="absolute inset-0 -z-10 bg-forest-900/55" data-id="element-409" data-mp="2mhnftveuylk8" />
          <div aria-hidden="true" className="absolute inset-y-0 left-0 -z-10 w-full bg-forest-900/30 lg:w-3/5" data-id="element-410" data-mp="2hjc6dlsh6d2j" />

          <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 md:py-36" data-id="element-411" data-mp="2on1xr1rb47cs">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            ease
          }} className="max-w-3xl" data-id="element-412" data-mp="2tld6ta9s2dty">
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-[#EAD9A8] ring-1 ring-white/20 backdrop-blur-sm" data-id="element-413" data-mp="33k2pv6deu4bs">
                <SparklesIcon className="h-4 w-4" aria-hidden="true" data-id="element-414" data-mp="2aucayv3yeyem" />
                Holiday Season
              </p>
              <h1 id="christmas-title" className="mt-7 font-display text-5xl font-medium leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl" data-id="element-415" data-mp="2fsxd502rns5p">
                Chacon’s Lawncare <span className="italic text-[#EAD9A8]" data-id="element-416" data-mp="1n4rpo5kbvp26">Christmas Decoration</span> Services
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/85 md:text-xl" data-id="element-417" data-mp="1s2jjx68g3n9j">
                Professional Christmas Light Design &amp; Installation
              </p>
              <div className="mt-10 flex flex-col gap-3 sm:flex-row" data-id="element-418" data-mp="58zdn41yg924">
                <a href={christmasQuoteHref} className="inline-flex h-14 items-center justify-center rounded-full bg-olive px-8 text-[13px] font-bold uppercase tracking-[0.08em] text-white shadow-[0_10px_30px_-12px_rgba(0,0,0,0.6)] transition-colors hover:bg-olive-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-forest-900" data-id="element-419" data-mp="7s9dez0ts07">
                  Get a Free Quote
                </a>
                <a href="#decorations" className="inline-flex h-14 items-center justify-center rounded-full bg-white/5 px-8 text-[13px] font-bold uppercase tracking-[0.08em] text-white ring-1 ring-white/50 backdrop-blur-sm transition-colors hover:bg-white/15" data-id="element-420" data-mp="38pg30lydtras">
                  View Decorations
                </a>
              </div>
            </motion.div>
          </div>
          <div aria-hidden="true" className="flex h-1.5 w-full" data-id="element-421" data-mp="17ldqolyvo2vk">
            <span className="flex-1 bg-olive" data-id="element-422" data-mp="sw7xbemq16vl" />
            <span className="flex-1 bg-[#9B2335]" data-id="element-423" data-mp="svex6wi9pfmf" />
            <span className="flex-1 bg-[#EAD9A8]" data-id="element-424" data-mp="xvarg415xsos" />
          </div>
        </section>

        <EstimateFormSection data-id="element-425" data-mp="jwbdprf6uy7e" />
        <ChristmasAboutSection data-id="element-426" data-mp="juzzexo4a821" />
        <ChristmasProcessSection data-id="element-427" data-mp="jyxjpmmfl5ni" />
        <ChristmasTestimonialsSection data-id="element-428" data-mp="ovxqy0v0oeg7" />

        <section id="lighting-options" aria-labelledby="options-title" className="scroll-mt-32 py-20 md:py-28" data-id="element-429" data-mp="owz54e7bxh25">
          <div className="mx-auto max-w-7xl px-5 sm:px-8" data-id="element-430" data-mp="a0hpvbw0riab">
            <div className="mx-auto max-w-2xl text-center" data-id="element-431" data-mp="ey9jcacbbcgm">
              <p className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-olive" data-id="element-432" data-mp="24co4kko4gh7d">
                <span aria-hidden="true" className="h-px w-8 bg-current" data-id="element-433" data-mp="29br9m543qnfc" />
                Choose Your Property Type
                <span aria-hidden="true" className="h-px w-8 bg-current" data-id="element-434" data-mp="3tubkbpqaisou" />
              </p>
              <h2 id="options-title" className="mt-5 font-display text-4xl font-medium leading-tight tracking-tight text-forest md:text-5xl" data-id="element-435" data-mp="3ovi6fw73czo5">
                Lighting for homes <span className="italic text-olive" data-id="element-436" data-mp="2n8tx0ia0qfo">&amp;</span> businesses.
              </h2>
              <div className="mt-6 flex items-center justify-center gap-6 text-sm font-semibold text-forest/70" data-id="element-437" data-mp="genc554d0nlo">
                <span className="flex items-center gap-2" data-id="element-438" data-mp="cdbtjjjwl2mz">
                  <HomeIcon className="h-4 w-4 text-olive" aria-hidden="true" data-id="element-439" data-mp="ccseqbpjuud6" /> Residential
                </span>
                <span aria-hidden="true" className="h-4 w-px bg-forest/20" data-id="element-440" data-mp="qagythbslxh6" />
                <span className="flex items-center gap-2" data-id="element-441" data-mp="lejhb7osml2t">
                  <Building2Icon className="h-4 w-4 text-olive" aria-hidden="true" data-id="element-442" data-mp="2s3ek8s38pns3" /> Commercial
                </span>
              </div>
            </div>

            <ul className="mt-14 grid gap-8 md:grid-cols-2" data-id="element-443" data-mp="2n77qrys164r1">
              {lightingCategories.map((category, index) => <motion.li key={category.slug} initial={{
              opacity: 0,
              y: 24
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.6,
              ease,
              delay: index * 0.1
            }} data-id="element-444" data-mp="w0ryezoomy2n" data-mpi={index}>
                  <LightingCategoryCard category={category} data-id="element-445" data-mp="1a1m8rnvaj93y" />
                </motion.li>)}
            </ul>
          </div>
        </section>

        <DecorationServicesSection data-id="element-446" data-mp="1exkrautxtwwg" />
        <ChristmasGallerySection data-id="element-447" data-mp="1jqdwtkx8d99l" />
        <ChristmasWhyChooseSection data-id="element-448" data-mp="1jr5sswuqp90e" />
        <ChristmasFaqSection data-id="element-449" data-mp="1ns7tli4b1t4h" />
      </main>
      <ChristmasFooter data-id="element-450" data-mp="1nr69yp983yb5" />
    </>;
}